import { useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import type { WarehouseDTO } from '@erp/rpc';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { Label } from '@/components/ui/label';

function warehouseLabel(warehouse: WarehouseDTO): string {
  return warehouse.name;
}

export function WarehouseAutocomplete({
  id,
  label,
  ariaLabel,
  value,
  options,
  disabled,
  onChange,
}: {
  id: string;
  label: ReactNode;
  ariaLabel: string;
  value: string;
  options: WarehouseDTO[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Combobox
        id={id}
        items={options}
        value={value || null}
        onValueChange={(next) => onChange(next ?? '')}
        deselectable
        search={search}
        onSearchChange={setSearch}
        resetSearchOnClose={false}
        getValue={(warehouse) => warehouse.id}
        getLabel={warehouseLabel}
        labels={pickerLabels({
          placeholder: gt('Choose a warehouse', {
            context: 'procurement order warehouse autocomplete placeholder',
          }),
          search: gt('Search warehouses…', {
            context: 'procurement order warehouse autocomplete search',
          }),
          empty: gt('No warehouses found.', {
            context: 'procurement order warehouse autocomplete empty state',
          }),
        })}
        aria-label={ariaLabel}
        disabled={disabled}
        contentClassName="w-full"
        indicator="none"
        renderItem={(warehouse) => (
          <div className="min-w-0 py-0.5">
            <div className="truncate font-medium">{warehouse.name}</div>
            <div className="truncate text-xs text-muted-foreground">
              {[warehouse.division?.name, warehouse.type]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        )}
      />
    </div>
  );
}
